import React, { useMemo, useState } from "react";
import { Check, Coffee, Maximize, Shield, Snowflake, Wifi } from "lucide-react";
import styles from "./RoomCard.module.scss";
import montereyImg from "../assets/img/jeremy-huang-2QZ3AnkVTEo-unsplash.jpg";
import { RoomCardProps } from "../types/room";
import { DEFAULT_ROOM_SEED, getRoomSeedByType, type RoomSeed } from "../data/roomseed";
import { Modal } from "./Modal";
import { HotelBookingWidget } from "./HotelBookingWidget";

const ICONS = {
    Maximize,
    Snowflake,
    Coffee,
    Wifi,
    Shield,
} as const;

export const RoomCard: React.FC<RoomCardProps> = ({
    room,
    onReserve,
    staticCheckIn,
    staticCheckOut,
}) => {
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [activePhoto, setActivePhoto] = useState(0);
    const seed = getRoomSeedByType(room.type) || DEFAULT_ROOM_SEED;
    const cancellation = room.refundable ? "Free cancellation" : seed.cancellation;
    const parsedTotal = useMemo(() => String(room.price || "").replace(/[^0-9.]/g, "") || "0", [room.price]);
    const normalizedCheckIn = staticCheckIn || (room as any).checkInDate || (room as any).check_in_date || "";
    const normalizedCheckOut = staticCheckOut || (room as any).checkOutDate || (room as any).check_out_date || "";
    const photos = room?.images?.length ? room.images : [room?.image || seed.photo || montereyImg];
    const photo = photos[activePhoto] || photos[0];

    const bookingData = {
        hotel: {
            name: room.type || "Hotel",
            image: photo,
        },
        offers: [
            {
                checkInDate: normalizedCheckIn,
                checkOutDate: normalizedCheckOut,
                room: {
                    typeEstimated: {
                        category: room.type || "ROOM",
                    },
                },
                price: {
                    currency: room.currency || "",
                    total: parsedTotal,
                },
            },
        ],
    };

    const handleReserveClick = () => {
        onReserve?.(room);
        setIsBookingOpen(true);
    };

    return (
        <div className={`${styles.card} ${seed.bestMatch ? styles.cardBest : ""}`}>
            <div className={styles.photoWrap}>
                <img
                    src={photo}
                    alt={room?.type || "Room"}
                    className={styles.photo}
                />
                {photos.length > 1 && (
                    <div className={styles.photoDots}>
                        {photos.map((_, index) => (
                            <button
                                key={`${room.id}-photo-${index}`}
                                type="button"
                                className={`${styles.photoDot} ${index === activePhoto ? styles.photoDotActive : ''}`}
                                onClick={() => setActivePhoto(index)}
                                aria-label={`Show room photo ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
                {seed.bestMatch && <span className={styles.bestBadge}>Best match</span>}
            </div>

            <div className={styles.body}>
                <h4 className={styles.name}>{room.type}</h4>
                <p className={styles.meta}>{room.board || seed.beds} · Sleeps {room.sleeps}</p>

                <div className={styles.amenities}>
                    {seed.amenities.map((item: RoomSeed["amenities"][number]) => {
                        const Icon = ICONS[item.icon];
                        return (
                            <div key={item.label} className={styles.amenity}>
                                <Icon size={12} />
                                <span>{item.label}</span>
                            </div>
                        );
                    })}
                </div>

                <div className={`${styles.cancel} ${cancellation === "Free cancellation" ? styles.cancelFree : styles.cancelNonref}`}>
                    {cancellation === "Free cancellation" && <Check size={12} />}
                    <span>{cancellation}</span>
                </div>

                <div className={styles.footer}>
                    <div className={styles.priceBlock}>
                        <span className={styles.price}>{room.price}</span>
                    </div>

                    <button className={styles.reserveBtn} onClick={handleReserveClick}>
                        Reserve
                    </button>
                </div>
            </div>

            <Modal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)}>
                <HotelBookingWidget
                    booking={bookingData}
                    onCancel={() => setIsBookingOpen(false)}
                    onComplete={() => setIsBookingOpen(false)}
                    onViewBookings={() => setIsBookingOpen(false)}
                />
            </Modal>
        </div>
    );
};
